import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useGetConsolidationBasisOptionsMutation } from "@api/consolidation.api";
import {
    ConsolidationBasisOptionsResult,
    ConsolidationGroupResponse,
} from "@common/schemas/domains/accounting/consolidation.schema";
import { Entity } from "@common/types/entity";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import { Picker } from "@design-system/Picker/Picker.lib";
import {
    CONSOLIDATION_BASIS_LABELS,
    isLatestBasisOptionsRequest,
} from "../consolidation/consolidationBasis.helpers";
import { OwnershipEntityIdentity } from "../OwnershipEntityIdentity";
import {
    getConsolidationBasisEntityUuids,
    getConsolidationGroupBasis,
} from "./consolidationSettings.helpers";
import styles from "./Pane.module.scss";

export interface UpdateGroupConfigParams {
    groupId: string;
    ledgerIdsByEntityUuid: Record<string, string>;
}

export interface UpdateGroupConfigCallbacks {
    onError?: () => void;
}

interface ConsolidationBasisSelectorProps {
    workspaceId: string;
    group: ConsolidationGroupResponse;
    entitiesByUuid: Map<string, Entity>;
    isUpdating: boolean;
    onUpdateGroupConfig: (
        params: UpdateGroupConfigParams,
        callbacks?: UpdateGroupConfigCallbacks,
    ) => void;
}

export const ConsolidationBasisSelector: React.FC<
    ConsolidationBasisSelectorProps
> = ({
    workspaceId,
    group,
    entitiesByUuid,
    isUpdating,
    onUpdateGroupConfig,
}) => {
    const { mutate: getBasisOptions } =
        useGetConsolidationBasisOptionsMutation(workspaceId);
    const entityUuidsKey = getConsolidationBasisEntityUuids(group).join(",");
    const entityUuids = useMemo(
        () => entityUuidsKey.split(","),
        [entityUuidsKey],
    );
    const [ledgerOptionsByEntityUuid, setLedgerOptionsByEntityUuid] = useState<
        ConsolidationBasisOptionsResult["ledgerOptionsByEntityUuid"]
    >({});
    const [ledgerIdsByEntityUuid, setLedgerIdsByEntityUuid] = useState<
        Record<string, string>
    >({});
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const latestRequestId = useRef(0);
    const latestGroup = useRef(group);

    useEffect(() => {
        latestGroup.current = group;
    }, [group]);

    const loadLedgerOptions = useCallback(() => {
        const requestId = latestRequestId.current + 1;
        latestRequestId.current = requestId;
        setLedgerOptionsByEntityUuid({});
        setLedgerIdsByEntityUuid({});
        setIsLoading(true);
        setHasError(false);
        getBasisOptions(
            { entityUuids },
            {
                onSuccess: (result) => {
                    if (
                        !isLatestBasisOptionsRequest(
                            requestId,
                            latestRequestId.current,
                        )
                    ) {
                        return;
                    }
                    const currentGroup = latestGroup.current;
                    const currentBasis =
                        getConsolidationGroupBasis(currentGroup);
                    const selectedLedgerIdsByEntityUuid: Record<
                        string,
                        string
                    > = {};
                    for (const entityUuid of entityUuids) {
                        const ledgerOptions =
                            result.ledgerOptionsByEntityUuid[entityUuid] ?? [];
                        const configuredLedgerId =
                            currentGroup.config?.ledgerIdsByEntityUuid?.[
                                entityUuid
                            ];
                        const selectedLedger =
                            ledgerOptions.find(
                                ({ id }) => id === configuredLedgerId,
                            ) ??
                            ledgerOptions.find(
                                ({ basis }) => basis === currentBasis,
                            ) ??
                            ledgerOptions[0];
                        if (selectedLedger) {
                            selectedLedgerIdsByEntityUuid[entityUuid] =
                                selectedLedger.id;
                        }
                    }
                    setLedgerOptionsByEntityUuid(
                        result.ledgerOptionsByEntityUuid,
                    );
                    setLedgerIdsByEntityUuid(selectedLedgerIdsByEntityUuid);
                    setIsLoading(false);
                },
                onError: () => {
                    if (
                        !isLatestBasisOptionsRequest(
                            requestId,
                            latestRequestId.current,
                        )
                    ) {
                        return;
                    }
                    setIsLoading(false);
                    setHasError(true);
                },
            },
        );
    }, [entityUuids, getBasisOptions]);

    useEffect(() => {
        loadLedgerOptions();
        return () => {
            latestRequestId.current += 1;
        };
    }, [group.id, loadLedgerOptions]);

    const updateLedger = (entityUuid: string, ledgerId: string) => {
        const previousLedgerIdsByEntityUuid = ledgerIdsByEntityUuid;
        const nextLedgerIdsByEntityUuid = {
            ...ledgerIdsByEntityUuid,
            [entityUuid]: ledgerId,
        };
        setLedgerIdsByEntityUuid(nextLedgerIdsByEntityUuid);
        onUpdateGroupConfig(
            {
                groupId: group.id,
                ledgerIdsByEntityUuid: nextLedgerIdsByEntityUuid,
            },
            {
                // Roll the optimistic selection back if the update fails so the
                // dropdown doesn't keep showing an un-persisted ledger.
                onError: () =>
                    setLedgerIdsByEntityUuid(previousLedgerIdsByEntityUuid),
            },
        );
    };

    if (isLoading) {
        return <div className={styles.emptyHint}>Loading entity ledgers…</div>;
    }

    if (hasError) {
        return (
            <HStack gap={8} align="center">
                <span className={styles.emptyHint}>
                    Couldn&apos;t load entity ledgers
                </span>
                <Button variant="primary" size="sm" onClick={loadLedgerOptions}>
                    Try again
                </Button>
            </HStack>
        );
    }

    return (
        <>
            {entityUuids.map((entityUuid) => {
                const ledgerOptions =
                    ledgerOptionsByEntityUuid[entityUuid] ?? [];
                const selectedLedgerId = ledgerIdsByEntityUuid[entityUuid];
                const entity = entitiesByUuid.get(entityUuid);
                const selectedLedger = ledgerOptions.find(
                    ({ id }) => id === selectedLedgerId,
                );
                return (
                    <div key={entityUuid} className={styles.detailRow}>
                        <span className={styles.detailLabel}>
                            {entity ? (
                                <OwnershipEntityIdentity entity={entity} />
                            ) : (
                                "Unknown entity"
                            )}
                        </span>
                        <span className={styles.detailValue}>
                            <div className={styles.selectDetail}>
                                <Picker.Select
                                    items={ledgerOptions.map((ledger) => ({
                                        value: ledger.id,
                                        label: CONSOLIDATION_BASIS_LABELS[
                                            ledger.basis
                                        ],
                                    }))}
                                    value={selectedLedgerId ?? null}
                                    disabled={
                                        ledgerOptions.length === 0 || isUpdating
                                    }
                                    onValueChange={(ledgerId) =>
                                        ledgerId &&
                                        ledgerId !== selectedLedgerId &&
                                        updateLedger(entityUuid, ledgerId)
                                    }
                                    placeholder="No ledger available"
                                    renderValue={() =>
                                        selectedLedger
                                            ? CONSOLIDATION_BASIS_LABELS[
                                                  selectedLedger.basis
                                              ]
                                            : "No ledger available"
                                    }
                                    trigger={
                                        <Picker.Button
                                            variant="primary"
                                            size="md"
                                            className={styles.selectTrigger}
                                        />
                                    }
                                />
                            </div>
                        </span>
                    </div>
                );
            })}
        </>
    );
};
